import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import styled from "styled-components";
import { standardInputStyles, standardLabelStyles } from "../general.styled";

export const DropdownContainer = styled.div<{
  $size?: "small" | "medium";
  width?: number;
  marginLeft?: number;
  marginRight?: number;
  marginTop?: number;
  marginBottom?: number;
}>`
  position: relative;
  width: ${(p) => (p.width ? `${p.width}px` : "100%")};
  max-width: 250px; /* behåll samma som input */
  margin-bottom: ${(p) => (p.marginBottom !== undefined ? `${p.marginBottom}px` : "16px")};
  margin-right: ${(p) => (p.marginRight !== undefined ? `${p.marginRight}px` : "10px")};
  margin-left: ${(p) => (p.marginLeft !== undefined ? `${p.marginLeft}px` : "0px")};
  margin-top: ${(p) => (p.marginTop !== undefined ? `${p.marginTop}px` : "0px")};
`;

export const DropdownLabel = styled.label<{
  required?: boolean;
  hasValue?: boolean;
  isFocused?: boolean;
  disabled?: boolean;
  $size?: "small" | "medium";
}>`
  ${standardLabelStyles}

  /* Snyggare balans mellan label och innehåll */
  font-size: ${(p) => (p.$size === "small" ? FontSize.Tiny : FontSize.Small)};
  line-height: 1.2;
  z-index: 1; /* säkerställ att label ligger ovanför bakgrunden */
`;

export const DropdownSelect = styled.div<{
  fontSize?: FontSize;
  hasError?: boolean;
  hasValue?: boolean;
  isOpen?: boolean;
  disabled?: boolean;
  $size?: "small" | "medium";
}>`
  ${standardInputStyles}

  /* Padding anpassad för flytande label */
  padding: ${(p) =>
    p.$size === "small"
      ? p.hasValue
        ? "12px 12px 4px 12px"  
        : "16px 12px 8px 12px"  
      : p.hasValue
      ? "24px 16px 8px 16px"
      : "16px 16px"};

  /* Kompakt höjd men med minsta höjd så en 50px rad funkar med label ovan */
  min-height: ${(p) => (p.$size === "small" ? "40px" : "44px")};

  /* Fontstorlek följer size om ingen explicit fontSize skickas in */
  font-size: ${(p) =>
    p.fontSize ?? (p.$size === "small" ? FontSize.Small : FontSize.Standard)};

  /* Typografi för bättre vertikal balans */
  line-height: ${(p) => (p.$size === "small" ? "20px" : "22px")};

  /* Disabled-stil harmoniserad med inputs */
  background: ${(p) => (p.disabled ? Color.LightGrey02 : "#f8f9fa")};
  color: ${(p) => (p.disabled ? Color.DarkGrey : Color.InputTextColor)};
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.disabled ? 0.6 : 1)};

  &:hover {
    background: ${(p) => (p.disabled ? Color.LightGrey02 : "#f1f3f4")};
  }

  /* Focus/öppet tillstånd – samma padding så labeln inte hoppar */
  ${(p) =>
    (p.isOpen || !p.disabled) &&
    `
    &:focus-within {
      background: ${p.disabled ? Color.LightGrey02 : Color.White};
      border-color: ${p.disabled ? "transparent" : "#e5e7eb"};
      box-shadow: ${p.disabled ? "none" : "0 0 0 1px rgba(59, 130, 246, 0.1)"};
      padding: ${
        p.$size === "small" ? "12px 12px 4px 12px" : p.hasValue ? "24px 16px 8px 16px" : "16px 16px"
      };
    }
  `}
`;

export const DropdownArrow = styled.div<{
  isOpen?: boolean;
  $size?: "small" | "medium";
}>`
  position: absolute;
  right: ${(p) => (p.$size === "small" ? "12px" : "16px")};
  top: 50%;
  transform: translateY(-50%)
    rotate(${(props) => (props.isOpen ? "180deg" : "0deg")});
  width: 0;
  height: 0;
  border-left: ${(p) => (p.$size === "small" ? "4px" : "5px")} solid transparent;
  border-right: ${(p) => (p.$size === "small" ? "4px" : "5px")} solid transparent;
  border-top: ${(p) => (p.$size === "small" ? "4px" : "5px")} solid #6b7280;
  transition: transform 0.2s ease;
  pointer-events: none;
`;

export const DropdownList = styled.ul<{ isOpen?: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${Color.White};
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin: 4px 0 0 0;
  padding: 0;
  list-style: none;
  display: ${(props) => (props.isOpen ? "block" : "none")};
`;

export const DropdownItem = styled.li<{ $size?: "small" | "medium" }>`
  padding: ${(p) => (p.$size === "small" ? "10px 12px" : "12px 16px")};
  font-family: ${FontFamily.MontserratRegular}, Helvetica;
  font-size: ${(p) => (p.$size === "small" ? FontSize.Small : FontSize.Small)};
  color: ${Color.InputTextColor};
  cursor: pointer;
  transition: background-color 0.2s ease;
  line-height: ${(p) => (p.$size === "small" ? "20px" : "22px")};

  &:hover {
    background-color: #f8f9fa;
  }

  &:first-child {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }

  &:last-child {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

export const SelectedValue = styled.span<{ $size?: "small" | "medium" }>`
  color: ${Color.InputTextColor};
  font-weight: 400;
  font-size: ${(p) =>
    p.$size === "small" ? FontSize.Small : FontSize.Standard};
  font-family: ${FontFamily.MontserratRegular}, Helvetica;
  line-height: ${(p) => (p.$size === "small" ? "20px" : "22px")};
  display: block;
`;

export const PlaceholderText = styled.span<{ $size?: "small" | "medium" }>`
  color: #9ca3af;
  font-weight: 300;
  font-size: ${(p) =>
    p.$size === "small" ? FontSize.Small : FontSize.Standard};
  font-family: ${FontFamily.MontserratRegular}, Helvetica;
  line-height: ${(p) => (p.$size === "small" ? "20px" : "22px")};
  display: block;
`;